import backendConstants from "src/app/constants/backend.constants";
import localStorageConstants from "src/app/constants/localStorage.constants";
import {
  StagedModel,
  StagedParent,
} from "src/app/models/screens/userManagement.model";
import axios, { setToken } from "src/app/utils/axiosInstance.util";

export const login = async (email: string, password: string) => {
  try {
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.AUTH_URL}${backendConstants.LOGIN}`,
      {
        email: email,
        password: password,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return {
      success: true,
      token: response.data.token,
      suspended: response.data.suspended,
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const checkTokenValidity = async () => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.AUTH_URL}${backendConstants.CHECK_TOKEN_VALIDITY}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return {
      success: true,
      role: response.data.role,
      isSuspended: response.data.isSuspended,
      preferences: response.data.preferences,
      isFirstTimeVisiting: response.data.isFirstTimeVisiting,
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getWeeklyTemplate = async (childId?: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.WEEKLY_AVAILABILITY_TEMPLATE}${childId ? `?childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, weeklyTemplate: response.data.weeklyTemplate };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const addRowToWeeklyTemplate = async (
  dayOfWeek: string,
  from: number,
  to: number,
  tag: string,
  description: string | undefined,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.WEEKLY_AVAILABILITY_TEMPLATE}`,
      {
        dayOfWeek: dayOfWeek,
        from: from,
        to: to,
        tag: tag,
        description: description,
        childId: childId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const deleteRowFromWeeklyTemplate = async (
  dayOfWeek: string,
  from: number,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.delete(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.WEEKLY_AVAILABILITY_TEMPLATE}?dayOfWeek=${dayOfWeek}&from=${from}${childId ? `&childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getCustomizedDateSlots = async (childId?: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.DATE_AVAILABILITY}${childId ? `?childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, customizedSlots: response.data.customizedSlots };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const addRowToDateCustomizedSlot = async (
  date: string,
  from: number,
  to: number,
  tag: string,
  description: string | undefined,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.DATE_AVAILABILITY}`,
      {
        date: date,
        from: from,
        to: to,
        tag: tag,
        description: description,
        childId: childId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const deleteRowFromDateCustomSlots = async (
  date: string,
  from: number,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.delete(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.DATE_AVAILABILITY}?date=${date}&from=${from}${childId ? `&childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const resetSlotsToWeeklyTemplate = async (
  date: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.DATE_TO_TEMPLATE}`,
      {
        date: date,
        childId: childId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getUserTags = async (childId?: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.TAGS}${childId ? `?childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, tags: response.data.tags };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const addUserTag = async (
  tagText: string,
  tagColor: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.TAGS}`,
      {
        tagText: tagText,
        tagColor: tagColor,
        childId: childId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, tags: response.data.tags };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const searchUsers = async (query: string, childId?: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.SEARCH}?query=${query}${childId ? `&childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, users: response.data.users };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getUserFreeTimeslots = async (
  userId: string,
  date: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.FREE_SLOTS}?userId=${userId}&date=${date}${childId ? `&childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, freeTimeslots: response.data.freeTimeslots };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const createBookingRequest = async (
  recipientUserId: string,
  date: string,
  from: number,
  to: number,
  description: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.BOOKING_REQUEST}`,
      {
        recipient: recipientUserId,
        date: date,
        from: from,
        to: to,
        description: description,
        childId: childId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, request: response.data.request };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getReceivedBookingRequests = async (
  childId?: string,
  userId?: string,
) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.BOOKING_REQUEST}${childId ? `?childId=${childId}` : ""}${userId ? `?userId=${userId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, requests: response.data.requests };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const acceptBookingRequest = async (
  requestId: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.BOOKING_REQUEST}`,
      {
        requestId: requestId,
        accept: true,
        childId: childId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, updatedRequests: response.data.updatedRequests };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const rejectBookingRequest = async (
  requestId: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.BOOKING_REQUEST}`,
      {
        requestId: requestId,
        accept: false,
        childId: childId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, updatedRequest: response.data.updatedRequest };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getAcceptedRequestsForDate = async (
  date: string,
  childId?: string,
  userId?: string,
) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.BOOKING_REQUEST}${backendConstants.ACCEPTED}?date=${date}${childId ? `&childId=${childId}` : ""}${userId ? `&userId=${userId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, requests: response.data.requests };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const createEmergencyBookingRequest = async (
  recipientUserId: string,
  date: string,
  from: number,
  description: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.BOOKING_REQUEST}${backendConstants.EMERGENCY_REQUEST}`,
      {
        recipient: recipientUserId,
        date: date,
        from: from,
        description: description,
        childId: childId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, request: response.data.request };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const createGuestUser = async (
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  password: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.AUTH_URL}${backendConstants.REGISTER}${backendConstants.GUEST}`,
      {
        firstName: firstName,
        lastName: lastName,
        email: email,
        phoneNumber: phoneNumber,
        password: password,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const createModelAndLink = async (
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  password: string,
  dateOfBirth: string,
  gender: string,
  linkToParentId: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.AUTH_URL}${backendConstants.REGISTER}${backendConstants.MODEL}`,
      {
        firstName: firstName,
        lastName: lastName,
        email: email,
        phoneNumber: phoneNumber,
        password: password,
        dateOfBirth: dateOfBirth,
        gender: gender,
        linkToParentId: linkToParentId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const createParentAndLink = async (
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  address: string,
  password: string,
  linkToChildId: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.AUTH_URL}${backendConstants.REGISTER}${backendConstants.PARENT}`,
      {
        firstName: firstName,
        lastName: lastName,
        email: email,
        phoneNumber: phoneNumber,
        address: address,
        password: password,
        linkToChildId: linkToChildId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getLinkableModels = async (query: string, parentId: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.MODEL}${backendConstants.LINKS}?query=${query}&parentId=${parentId}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, models: response.data.models };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getLinkableParents = async (query: string, modelId: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.PARENT}${backendConstants.LINKS}?query=${query}&modelId=${modelId}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, parents: response.data.parents };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getRelatedUsers = async (query: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.LINKS}?query=${query}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, related: response.data.related };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const linkExistingUsers = async (modelId: string, parentId: string) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.LINKS}`,
      {
        modelId: modelId,
        parentId: parentId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const saveBatchUsers = async (
  models: StagedModel[],
  parents: StagedParent[],
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.AUTH_URL}${backendConstants.REGISTER}`,
      {
        models: models,
        parents: parents,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const searchModels = async (query: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.MODEL}?query=${query}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, users: response.data.users };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getParentChildren = async () => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.CHILDREN}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, children: response.data.children };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getManagedUserDetails = async (userId: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.DETAILS}?userId=${userId}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, user: response.data.user };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const updateManagedUserProfile = async (
  userId: string,
  payload: {
    firstName: string;
    lastName: string;
    email: string;
    gender?: string;
    dateOfBirth?: string;
    address?: string;
    phoneNumber?: string;
  },
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.DETAILS}`,
      {
        userId: userId,
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        gender: payload.gender,
        dateOfBirth: payload.dateOfBirth,
        address: payload.address,
        phoneNumber: payload.phoneNumber,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, user: response.data.user };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const setManagedUserCloudStorageLimit = async (
  userId: string,
  maxStorageGb: number,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.CLOUD_STORAGE_LIMIT}`,
      {
        userId: userId,
        maxStorageGb: maxStorageGb,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, user: response.data.user };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const deactivateManagedUser = async (userId: string) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.DEACTIVATE}`,
      {
        userId: userId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, user: response.data.user };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const suspendManagedUser = async (userId: string, days: number) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.SUSPEND}`,
      {
        userId: userId,
        days: days,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, user: response.data.user };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getLogs = async (
  page: number,
  variation: string,
  userId?: string,
) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.LOGS_URL}?page=${page}&variation=${variation}${userId ? `&userId=${userId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return {
      success: true,
      logs: response.data.logs,
      pagination: response.data.pagination,
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const requestDetachFromParents = async () => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.DETACH_FROM_PARENTS}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const decideOnDetachFromParents = async (
  accept: boolean,
  userId: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.DETACH_FROM_PARENTS}`,
      {
        accept: accept,
        userId: userId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getDetachRequests = async () => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.DETACH_FROM_PARENTS}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, requests: response.data.requests };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const checkDetachAvailability = async () => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.DETACH_FROM_PARENTS}${backendConstants.AVAILABLE}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, available: response.data.available };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const removeAccessibleUser = async (
  userId: string,
  connectedUserId: string,
) => {
  try {
    await setToken();
    const response = await axios.delete(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.ACCESSIBLE_USERS}?userId=${userId}&connectedUserId=${connectedUserId}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const addAccessibleUser = async (
  userId: string,
  connectedUserId: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.ACCESSIBLE_USERS}`,
      {
        userId: userId,
        connectedUserId: connectedUserId,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getAccessibleUsersOptions = async (
  userId: string,
  queryString: string,
) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.SEARCH}${backendConstants.ACCESSIBLE_USERS}?userId=${userId}&queryString=${queryString}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, users: response.data.users };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const updateUserPreferences = async (preferences: {
  separateSites?: boolean;
  shareFilesWithChildren?: boolean;
}) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.PREFERENCES}`,
      {
        ...preferences,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};
export const createTask = async (
  date: string,
  title: string,
  description: string,
  tag: string,
  from: number,
  to: number,
  childId?: string,
  isPriority?: boolean,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.TASK_URL}`,
      {
        date,
        title,
        description,
        tag,
        from,
        to,
        childId,
        isPriority: isPriority ?? false,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, task: response.data.task };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getTasksForDate = async (
  date: string,
  childId?: string,
  userId?: string,
) => {
  try {
    await setToken();
    const params = new URLSearchParams({ date });
    if (childId) params.append("childId", childId);
    if (userId) params.append("userId", userId);
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.TASK_URL}?${params.toString()}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, tasks: response.data.tasks };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const updateTaskStatus = async (
  taskId: string,
  status: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.TASK_URL}${backendConstants.TASK_STATUS}`,
      { taskId, status, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, task: response.data.task };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const deleteTask = async (taskId: string, childId?: string) => {
  try {
    await setToken();
    const params = new URLSearchParams({ taskId });
    if (childId) params.append("childId", childId);
    const response = await axios.delete(
      `${backendConstants.BASE_URL}${backendConstants.TASK_URL}?${params.toString()}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const assignTaskToUser = async (
  taskId: string,
  recipientUserId: string,
  isDirectAssignment: boolean,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.TASK_URL}${backendConstants.TASK_ASSIGN}`,
      { taskId, recipientUserId, isDirectAssignment, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, taskRequest: response.data.taskRequest };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};
export const requestMoreTime = async (
  taskId: string,
  newDate: string,
  newFrom: number,
  newTo: number,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.TASK_URL}${backendConstants.TASK_MORE_TIME}`,
      { taskId, newDate, newFrom, newTo, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getReceivedTaskRequests = async (
  childId?: string,
  userId?: string,
) => {
  try {
    await setToken();
    const params = new URLSearchParams();
    if (childId) params.append("childId", childId);
    if (userId) params.append("userId", userId);
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.TASK_REQUEST_URL}${backendConstants.RECEIVED}${query}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, requests: response.data.requests };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getSentTaskRequests = async (
  childId?: string,
  userId?: string,
) => {
  try {
    await setToken();
    const params = new URLSearchParams();
    if (childId) params.append("childId", childId);
    if (userId) params.append("userId", userId);
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.TASK_REQUEST_URL}${backendConstants.SENT}${query}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, requests: response.data.requests };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const acceptTaskRequest = async (
  requestId: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.TASK_REQUEST_URL}`,
      { requestId, accept: true, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, updatedRequest: response.data.updatedRequest };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const rejectTaskRequest = async (
  requestId: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.TASK_REQUEST_URL}`,
      { requestId, accept: false, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, updatedRequest: response.data.updatedRequest };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const respondToMoreTimeRequest = async (
  taskRequestId: string,
  accept: boolean,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.TASK_REQUEST_URL}${backendConstants.TASK_MORE_TIME}`,
      { taskRequestId, accept, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, updatedRequest: response.data.updatedRequest };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getAllPayments = async (childId?: string, userId?: string) => {
  try {
    await setToken();
    const params = new URLSearchParams();
    if (childId) params.append("childId", childId);
    if (userId) params.append("userId", userId);
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.PAYMENT_URL}${query}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return {
      success: true,
      receivedPayments: response.data.receivedPayments,
      sentPayments: response.data.sentPayments,
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const composePayment = async (
  recipientId: string,
  amount: number,
  date: string,
  description: string,
  isAutomatic: boolean,
  intervalDays?: number,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.PAYMENT_URL}`,
      { recipientId, amount, date, description, isAutomatic, intervalDays },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return {
      success: true,
      payment: response.data.payment,
      automaticPayment: response.data.automaticPayment,
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getAutomaticPayments = async () => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.PAYMENT_URL}${backendConstants.AUTOMATIC}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return {
      success: true,
      automaticPayments: response.data.automaticPayments,
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getUpcomingPayments = async () => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.PAYMENT_URL}${backendConstants.UPCOMING}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return {
      success: true,
      upcomingPayments: response.data.upcomingPayments,
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const cancelAutomaticPayment = async (automaticPaymentId: string) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.PAYMENT_URL}${backendConstants.AUTOMATIC}${backendConstants.CANCEL}`,
      { automaticPaymentId: automaticPaymentId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return {
      success: true,
      automaticPayment: response.data.automaticPayment,
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getFilesItems = async (
  folderId?: string | null,
  childId?: string,
  userId?: string,
) => {
  try {
    await setToken();
    const params = new URLSearchParams();
    if (folderId) params.append("folderId", folderId);
    if (childId) params.append("childId", childId);
    if (userId) params.append("userId", userId);
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${query}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return {
      success: true,
      folders: response.data.folders,
      files: response.data.files,
      storageInfo: response.data.storageInfo,
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const createFilesFolder = async (
  name: string,
  parentId?: string | null,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.FOLDER}`,
      { name, parentId: parentId ?? null, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, folder: response.data.folder };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const RenameFilesFolder = async (
  folderId: string,
  name: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.FOLDER}`,
      { folderId, name, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, folder: response.data.folder };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const deleteFilesFolder = async (folderId: string, childId?: string) => {
  try {
    await setToken();
    const params = new URLSearchParams({ folderId });
    if (childId) params.append("childId", childId);
    const response = await axios.delete(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.FOLDER}?${params.toString()}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const uploadFilesFile = async (
  file: File,
  folderId?: string | null,
  childId?: string,
  onProgress?: (pct: number) => void,
) => {
  try {
    await setToken();
    const formData = new FormData();
    formData.append("file", file);
    if (folderId) formData.append("folderId", folderId);
    if (childId) formData.append("childId", childId);

    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.FILE}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
        onUploadProgress: (evt) => {
          if (evt.total && onProgress) {
            onProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, file: response.data.file };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const renameFilesFile = async (
  fileId: string,
  name: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.FILE}`,
      { fileId, name, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, file: response.data.file };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const deleteFilesFile = async (fileId: string, childId?: string) => {
  try {
    await setToken();
    const params = new URLSearchParams({ fileId });
    if (childId) params.append("childId", childId);
    const response = await axios.delete(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.FILE}?${params.toString()}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const downloadFilesFile = async (
  fileId: string,
  fileName: string,
  childId?: string,
  userId?: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    await setToken();
    const token: string | null = localStorage.getItem(
      localStorageConstants.TOKEN,
    );

    const params = new URLSearchParams({ fileId });
    if (childId) params.append("childId", childId);
    if (userId) params.append("userId", userId);

    const url = `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.DOWNLOAD}?${params.toString()}`;

    const res = await fetch(url, {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      return { success: false, error: json.error ?? "Download failed" };
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);

    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);

    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const addFilesAccess = async (
  itemId: string,
  itemType: "folder" | "file",
  targetUserId: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.ACCESS}`,
      { itemId, itemType, targetUserId, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const removeFilesACcess = async (
  itemId: string,
  itemType: "folder" | "file",
  targetUserId: string,
  childId?: string,
) => {
  try {
    await setToken();
    const params = new URLSearchParams({ itemId, itemType, targetUserId });
    if (childId) params.append("childId", childId);
    const response = await axios.delete(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.ACCESS}?${params.toString()}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const requestMoreCloudStorage = async (
  requestedGb: number,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.STORAGE_REQUEST}`,
      { requestedGb, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, request: response.data.request };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getFilesStorageRequests = async () => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.STORAGE_REQUEST}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, requests: response.data.requests };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const decideFilesStorageRequest = async (
  requestId: string,
  accept: boolean,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.STORAGE_REQUEST}`,
      { requestId, accept },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, updatedRequest: response.data.updatedRequest };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getVpsStorageInfo = async () => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.FILES}${backendConstants.VPS}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, vpsStorage: response.data.vpsStorage };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getCloudieItems = async (
  folderId?: string | null,
  childId?: string,
  viewAsUserId?: string,
) => {
  try {
    await setToken();
    const params = new URLSearchParams();
    if (folderId) params.append("folderId", folderId);
    if (childId) params.append("childId", childId);
    if (viewAsUserId) params.append("viewAsUserId", viewAsUserId);
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.CLOUDIE}${query}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return {
      success: true,
      folders: response.data.folders,
      files: response.data.files,
      storageInfo: response.data.storageInfo,
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getCloudieMediaUrl = (
  fileId: string,
  thumbnail: boolean,
  childId?: string,
  viewAsUserId?: string,
): string => {
  const token = localStorage.getItem(localStorageConstants.TOKEN) ?? "";
  const params = new URLSearchParams({ fileId, token });
  if (thumbnail) params.append("thumbnail", "1");
  if (childId) params.append("childId", childId);
  if (viewAsUserId) params.append("viewAsUserId", viewAsUserId);
  return `${backendConstants.BASE_URL}${backendConstants.CLOUDIE}${backendConstants.THUMBNAIL}?${params.toString()}`;
};

export const uploadCloudieFile = async (
  file: File,
  folderId?: string | null,
  childId?: string,
  onProgress?: (pct: number) => void,
) => {
  try {
    await setToken();
    const formData = new FormData();
    formData.append("file", file);
    if (folderId) formData.append("folderId", folderId);
    if (childId) formData.append("childId", childId);

    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.CLOUDIE}${backendConstants.FILE}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
        onUploadProgress: (evt) => {
          if (evt.total && onProgress) {
            onProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, file: response.data.file };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const createCloudieFolder = async (
  name: string,
  parentId?: string | null,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.CLOUDIE}${backendConstants.FOLDER}`,
      { name, parentId: parentId ?? null, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, folder: response.data.folder };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const toggleTaskPriority = async (taskId: string, childId?: string) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.TASK_URL}${backendConstants.PRIORITY}`,
      { taskId, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, task: response.data.task };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const addTaskCollaborator = async (
  taskId: string,
  collaboratorUserId: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.TASK_URL}${backendConstants.COLLABORATOR}`,
      { taskId, collaboratorUserId, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, task: response.data.task };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const removeTaskCollaborator = async (
  taskId: string,
  collaboratorUserId: string,
  childId?: string,
) => {
  try {
    await setToken();
    const params = new URLSearchParams({ taskId, collaboratorUserId });
    if (childId) params.append("childId", childId);
    const response = await axios.delete(
      `${backendConstants.BASE_URL}${backendConstants.TASK_URL}${backendConstants.COLLABORATOR}?${params.toString()}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, task: response.data.task };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};
export const createEvent = async (
  payload: {
    title: string;
    description: string;
    date: string;
    from: number;
    durationMinutes: number;
    location: string;
    isPublic: boolean;
    isPaid: boolean;
    participationFee: number;
  },
  bannerFile: File | null,
  childId?: string,
) => {
  try {
    await setToken();
    const formData = new FormData();
    formData.append("title", payload.title);
    formData.append("description", payload.description);
    formData.append("date", payload.date);
    formData.append("from", String(payload.from));
    formData.append("durationMinutes", String(payload.durationMinutes));
    formData.append("location", payload.location);
    formData.append("isPublic", payload.isPublic ? "true" : "false");
    formData.append("isPaid", payload.isPaid ? "true" : "false");
    if (payload.isPaid)
      formData.append("participationFee", String(payload.participationFee));
    if (childId) formData.append("childId", childId);
    if (bannerFile) formData.append("banner", bannerFile);
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
      },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, event: response.data.event };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getMyEvents = async (childId?: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.MINE}${childId ? `?childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, events: response.data.events };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getPublicEvents = async (childId?: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.PUBLIC}${childId ? `?childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, events: response.data.events };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getEventDetail = async (eventId: string, childId?: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.DETAILS}?eventId=${eventId}${childId ? `&childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return {
      success: true,
      event: response.data.event,
      participants: response.data.participants,
      viewerIsCreator: response.data.viewerIsCreator,
      viewerIsParticipant: response.data.viewerIsParticipant,
      viewerHasPendingJoinRequest: response.data.viewerHasPendingJoinRequest,
      eventHasFinished: response.data.eventHasFinished,
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getEventBannerUrl = (eventId: string): string => {
  const userToken = localStorage.getItem(localStorageConstants.TOKEN);
  return `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.BANNER}?eventId=${eventId}&token=${userToken}`;
};

export const searchEventInvitees = async (
  eventId: string,
  query: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.SEARCH}?eventId=${eventId}&query=${encodeURIComponent(query)}${childId ? `&childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, users: response.data.users };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const sendEventInvitation = async (
  eventId: string,
  recipientId: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.INVITATIONS}`,
      { eventId, recipientId, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, invitation: response.data.invitation };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getReceivedEventInvitations = async (childId?: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.INVITATIONS}${backendConstants.RECEIVED}${childId ? `?childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, invitations: response.data.invitations };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const respondToEventInvitation = async (
  invitationId: string,
  accept: boolean,
  companionUserIds: string[] | undefined,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.INVITATIONS}`,
      { invitationId, accept, companionUserIds, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, invitation: response.data.invitation };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const requestToJoinPublicEvent = async (
  eventId: string,
  companionUserIds: string[] | undefined,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.JOIN}`,
      { eventId, companionUserIds, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, joinRequest: response.data.joinRequest };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getReceivedEventJoinRequests = async (childId?: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.JOIN}${backendConstants.RECEIVED}${childId ? `?childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, joinRequests: response.data.joinRequests };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const respondToEventJoinRequest = async (
  joinRequestId: string,
  accept: boolean,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.JOIN}`,
      { joinRequestId, accept, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, joinRequest: response.data.joinRequest };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const setEventParticipantMediaAccess = async (
  eventId: string,
  participantId: string,
  allow: boolean,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.MEDIA_ACCESS}`,
      { eventId, participantId, allow, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return {
      success: true,
      excludedMediaUserIds: response.data.excludedMediaUserIds,
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getEventsForDate = async (date: string, childId?: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.SCHEDULE}?date=${date}${childId ? `&childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, events: response.data.events };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const cancelEvent = async (eventId: string, childId?: string) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.CANCEL}`,
      { eventId, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, event: response.data.event };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const rescheduleEvent = async (
  eventId: string,
  date: string,
  from: number,
  durationMinutes: number,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.patch(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.RESCHEDULE}`,
      { eventId, date, from, durationMinutes, childId },
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, event: response.data.event };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const withdrawFromEvent = async (
  eventId: string,
  reason: string,
  childId?: string,
) => {
  try {
    await setToken();
    const response = await axios.post(
      `${backendConstants.BASE_URL}${backendConstants.EVENT_URL}${backendConstants.WITHDRAW}`,
      { eventId, reason, childId },
    );
    if (response.status >= 400)
      return {
        success: false,
        accepted: response.data?.accepted === true,
        error: response.data.error,
      };
    return {
      success: true,
      accepted: response.data?.accepted !== false,
      event: response.data.event,
    };
  } catch (error: any) {
    return { success: false, accepted: false, error: error.message };
  }
};

export const getConnectedRelatives = async (childId?: string) => {
  try {
    await setToken();
    const response = await axios.get(
      `${backendConstants.BASE_URL}${backendConstants.USER_URL}${backendConstants.CONNECTED_RELATIVES}${childId ? `?childId=${childId}` : ""}`,
    );
    if (response.status >= 400)
      return { success: false, error: response.data.error };
    return { success: true, relatives: response.data.relatives };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};
